/**
 * Public email subscription management (CAN-SPAM / RFC 8058).
 *
 * Two paths — same page, different behaviour based on the URL:
 *   /email/unsubscribe?token=…&cat=all|reminders|marketing|follow_ups
 *       → auto-POST to /public/email/unsubscribe, show "You've been
 *         unsubscribed" + a link to Manage preferences.
 *   /email/preferences?token=…
 *       → fetch current prefs via /public/email/prefs, show granular
 *         toggles + a master "Unsubscribe from everything" switch.
 *
 * No auth required — the HMAC token encodes workspace_id + email.
 */
import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_LABELS = {
  reminders:  { title: "Shoot reminders",      hint: "Day-before and day-of reminders for a confirmed booking." },
  marketing:  { title: "Offers & announcements", hint: "Occasional promos, abandoned-cart nudges, discount codes." },
  follow_ups: { title: "Post-shoot follow-ups", hint: "Thank-you notes, review requests, next-shoot ideas." },
};

export default function EmailSubscriptionPage({ mode = "preferences" }) {
  const loc = useLocation();
  const nav = useNavigate();
  const qs = useMemo(() => new URLSearchParams(loc.search), [loc.search]);
  const token = qs.get("token") || "";
  const cat = qs.get("cat") || "all";
  const [state, setState] = useState("loading"); // loading / ready / unsubbed / error
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  // Unsubscribe flow — one POST on mount, then show confirmation.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (mode !== "unsubscribe") return;
      if (!token) { setState("error"); setError("Missing unsubscribe token."); return; }
      try {
        const { data } = await axios.post(`${BACKEND}/api/v1/public/email/unsubscribe`,
          { token, cat }, { headers: { "Content-Type": "application/json" } });
        if (!cancelled) {
          setData(data);
          setState("unsubbed");
        }
      } catch (e) {
        if (!cancelled) {
          setError(e?.response?.data?.detail || "Could not process your unsubscribe request.");
          setState("error");
        }
      }
    })();
    return () => { cancelled = true; };
  }, [mode, token, cat]);

  // Preferences flow — fetch current state.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (mode !== "preferences") return;
      if (!token) { setState("error"); setError("Missing or invalid token."); return; }
      try {
        const { data } = await axios.get(`${BACKEND}/api/v1/public/email/prefs?token=${encodeURIComponent(token)}`);
        if (!cancelled) {
          setData(data);
          setState("ready");
        }
      } catch (e) {
        if (!cancelled) {
          setError(e?.response?.data?.detail || "Could not load your preferences.");
          setState("error");
        }
      }
    })();
    return () => { cancelled = true; };
  }, [mode, token]);

  const savePrefs = async (nextPrefs) => {
    try {
      const { data } = await axios.post(`${BACKEND}/api/v1/public/email/prefs`,
        { token, prefs: nextPrefs }, { headers: { "Content-Type": "application/json" } });
      setData((d) => ({ ...d, prefs: data.prefs }));
      toast.success("Preferences updated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update preferences");
    }
  };

  const togglePref = (key, next) => {
    const curr = data?.prefs || {};
    savePrefs({ ...curr, [key]: next });
  };

  const unsubAll = () => savePrefs({ all: false });
  const resubAll = () => savePrefs({ all: true });

  // ─── Render ──────────────────────────────────────────────────────
  const studioName = data?.studio_name || data?.legal_entity || "This studio";

  return (
    <div className="min-h-screen bg-[#FBF7F0] flex items-center justify-center px-4 py-16"
          style={{ fontFamily: "Georgia, 'Cormorant Garamond', serif" }}>
      <div className="w-full max-w-xl bg-white border border-[#F3ECE0] p-10 text-[#241A11]"
            data-testid="email-sub-page">
        <div className="text-[11px] uppercase tracking-widest text-[#5C4A38] mb-2">
          Email preferences
        </div>
        <h1 className="text-3xl font-medium mb-6" data-testid="email-sub-studio-name">
          {studioName}
        </h1>

        {state === "loading" && (
          <div className="text-sm text-muted-foreground" data-testid="email-sub-loading">
            Loading…
          </div>
        )}

        {state === "error" && (
          <div className="bg-rose-50 border border-rose-200 text-rose-900 px-4 py-3 text-sm"
                data-testid="email-sub-error">
            {error}
          </div>
        )}

        {state === "unsubbed" && (
          <div className="space-y-5" data-testid="email-sub-success">
            <div className="bg-emerald-50 border border-emerald-200 text-emerald-900 px-4 py-3 text-sm">
              {cat === "all"
                ? "You've been unsubscribed from all non-essential emails."
                : `You've been unsubscribed from ${CATEGORY_LABELS[cat]?.title || cat}.`}
              {" "}You will continue to receive essential booking confirmations and receipts.
            </div>
            <p className="text-sm text-[#5C4A38] leading-relaxed">
              Changed your mind? Open the preferences page to pick exactly which emails you want.
            </p>
            <button
              type="button"
              onClick={() => nav(`/email/preferences?token=${encodeURIComponent(token)}`)}
              className="inline-block bg-[#241A11] text-[#FBF7F0] px-5 py-2.5 text-[11px] uppercase tracking-[3px] font-semibold border border-[#241A11] hover:bg-[#3a2a1c]"
              data-testid="email-sub-manage-prefs-link">
              Manage individual preferences →
            </button>
          </div>
        )}

        {state === "ready" && data?.prefs && (
          <div className="space-y-6" data-testid="email-prefs-form">
            <p className="text-sm text-[#5C4A38] leading-relaxed">
              Managing emails for{" "}
              <span className="font-semibold text-[#241A11]">{data.email}</span>.
              You will always receive essential booking confirmations and receipts —
              these settings only affect marketing and reminder emails.
            </p>

            <div className="space-y-3">
              {Object.entries(CATEGORY_LABELS).map(([key, meta]) => {
                const checked = data.prefs[key] !== false && data.prefs.all !== false;
                return (
                  <label
                    key={key}
                    className="flex items-start gap-3 border border-[#F3ECE0] p-4 cursor-pointer hover:bg-[#FBF7F0]"
                    data-testid={`email-pref-row-${key}`}>
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={(e) => togglePref(key, e.target.checked)}
                      disabled={data.prefs.all === false}
                      className="mt-1 h-4 w-4"
                      data-testid={`email-pref-toggle-${key}`}
                    />
                    <span className="flex-1">
                      <span className="block text-sm font-semibold text-[#241A11]">
                        {meta.title}
                      </span>
                      <span className="block text-xs text-[#5C4A38] mt-1">
                        {meta.hint}
                      </span>
                    </span>
                  </label>
                );
              })}
            </div>

            <div className="border-t border-[#F3ECE0] pt-6">
              {data.prefs.all === false ? (
                <button
                  type="button"
                  onClick={resubAll}
                  className="w-full bg-[#241A11] text-[#FBF7F0] px-5 py-3 text-[11px] uppercase tracking-[3px] font-semibold border border-[#241A11] hover:bg-[#3a2a1c]"
                  data-testid="email-pref-resub-all">
                  Resubscribe to all emails
                </button>
              ) : (
                <button
                  type="button"
                  onClick={unsubAll}
                  className="w-full bg-white text-[#241A11] px-5 py-3 text-[11px] uppercase tracking-[3px] font-semibold border border-[#241A11] hover:bg-[#241A11] hover:text-[#FBF7F0]"
                  data-testid="email-pref-unsub-all">
                  Unsubscribe from everything (non-essential)
                </button>
              )}
            </div>
          </div>
        )}

        <div className="mt-10 pt-6 border-t border-[#F3ECE0] text-[11px] text-[#8A7A66] text-center leading-relaxed">
          {data?.legal_entity && (
            <div className="font-semibold uppercase tracking-widest text-[#5C4A38] mb-1">
              {data.legal_entity}
            </div>
          )}
          Booking Experience powered by{" "}
          <a href="https://acquilix.com" target="_blank" rel="noopener noreferrer"
             className="text-[#241A11] font-semibold hover:underline">Acquilix</a>
          <sup className="text-[9px]">™</sup>
        </div>
      </div>
    </div>
  );
}
